import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "../services/api";
import { 
  ChevronLeft, 
  Plus, 
  Check, 
  Gift, 
  Zap,
  ArrowRight,
  Loader2,
  Sparkles
} from "lucide-react";
import AuthModal from "../components/AuthModal";

interface Question {
  id: number;
  title: string;
  weight: number;
  image: string | null;
}

const Accessories: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceId, variantId } = useParams();

  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const { groupId } = location.state || {};

  const [questions, setQuestions] = useState<Question[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  // --- CORE LOGIC ---
  useEffect(() => {
    if (!groupId) {
      setLoading(false);
      return;
    }

    api.get(`/inspection/group/${groupId}/accessory`)
      .then((data) => setQuestions(data || []))
      .catch((err) => console.error("Accessory Fetch Error:", err))
      .finally(() => setLoading(false));
  }, [groupId]);

  const toggle = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const goToPrice = () => {
    const finalData = {
      ...location.state,
      accessoryAnswers: selected
    };
    navigate(`/sell/price/${deviceId}/${variantId}`, { state: finalData });
  };

  const handleShowPrice = () => {
    const token = localStorage.getItem("user_token");
    if (!token) {
      setIsAuthOpen(true);
    } else {
      goToPrice();
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-10 h-10 text-[#363C90] animate-spin" />
        <p className="text-slate-400 text-sm font-bold uppercase tracking-widest italic">Valuating Accessories...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-40">
      
      {/* --- SLIM HEADER --- */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-[1000px] mx-auto px-4 py-4 md:py-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-slate-400 hover:text-[#363C90] transition-colors mb-3 text-[10px] font-black uppercase tracking-widest"
          >
            <ChevronLeft size={14} /> Back to Age Selection
          </button>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight leading-none">Original Assets</h1>
              <p className="text-slate-400 text-[13px] font-medium mt-1.5 italic">Select items you have to maximize your quote.</p>
            </div>

            <div className="hidden md:flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-xl border border-amber-100 text-amber-600">
              <Sparkles size={16} />
              <span className="text-[10px] font-black uppercase tracking-wider text-amber-700">Boosts Resale Value</span>
            </div>
          </div>
        </div>
      </div>

      {/* --- ACCESSORIES GRID --- */}
      <div className="max-w-[1000px] mx-auto px-4 mt-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
          {questions.map((item) => {
            const isActive = selected.includes(item.id);
            return (
              <div
                key={item.id}
                onClick={() => toggle(item.id)}
                className={`
                  group relative cursor-pointer bg-white rounded-2xl p-5 border transition-all duration-200 flex flex-col items-center text-center
                  ${isActive 
                    ? "border-[#363C90] bg-indigo-50/30 shadow-md ring-1 ring-[#363C90]" 
                    : "border-slate-100 hover:border-slate-200 hover:shadow-lg"}
                `}
              >
                {/* Sharp Check Indicator */}
                <div className={`
                  absolute top-3 right-3 w-5 h-5 rounded-lg border flex items-center justify-center transition-all
                  ${isActive ? "bg-[#363C90] border-[#363C90]" : "bg-white border-slate-200"}
                `}>
                  {isActive ? <Check size={12} className="text-white" strokeWidth={4} /> : <Plus size={12} className="text-slate-300" />}
                </div>

                <div className={`w-14 h-14 md:w-16 md:h-16 mb-4 rounded-xl flex items-center justify-center transition-colors border ${
                   isActive ? "bg-white border-indigo-100" : "bg-slate-50 border-slate-50"
                }`}>
                  {item.image ? (
                    <img src={item.image} alt={item.title} className="w-full h-full object-contain p-2 group-hover:scale-110 transition-transform duration-300" />
                  ) : (
                    <Gift className={isActive ? "text-[#363C90]" : "text-slate-200"} size={24} />
                  )}
                </div>

                <h3 className={`text-[12px] md:text-[13px] font-bold leading-tight px-1 ${isActive ? "text-[#363C90]" : "text-slate-600"}`}>
                  {item.title}
                </h3>
              </div>
            );
          })}
        </div>
      </div>

      {/* --- FINAL ACTION FOOTER --- */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-slate-100 p-6 z-40">
        <div className="max-w-[1000px] mx-auto">
          <button
            onClick={handleShowPrice}
            className="w-full py-5 bg-[#363C90] hover:bg-[#2a2f70] text-white rounded-2xl font-black text-sm flex items-center justify-center gap-3 transition-all hover:scale-[1.01] shadow-xl shadow-indigo-900/20 uppercase tracking-[2px]"
          >
            GET FINAL QUOTE <Zap size={18} className="text-amber-400 fill-amber-400" />
          </button>
          
          <div className="flex items-center justify-center gap-6 mt-4 opacity-50">
             <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-500">
               <div className="w-1 h-1 rounded-full bg-emerald-500" /> Safe
             </div>
             <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-500">
               <div className="w-1 h-1 rounded-full bg-emerald-500" /> Secure
             </div>
             <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-500">
               <div className="w-1 h-1 rounded-full bg-emerald-500" /> Instant
             </div>
          </div>
        </div>
      </div>

      {/* --- AUTH MODAL --- */}
      <AuthModal 
        isOpen={isAuthOpen} 
        onClose={() => setIsAuthOpen(false)} 
        onSuccess={() => {
          setIsAuthOpen(false);
          goToPrice();
        }} 
      />
    </div>
  );
};

export default Accessories;